import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { AlertCircle, Check, Circle, X } from 'lucide-react'

import type { ToolActivity } from './protocol'
import styles from './ActivityTimelineDrawer.module.scss'

type TimelineStep = {
  id: string
  title: string
  detail?: string
  status: ToolActivity['status']
}

type TimelineLanguage = 'it' | 'en'

type LocalizedCopy = {
  title: string
  detail: string
}

const ACTIVITY_COPY: Array<{
  matches: (name: string) => boolean
  copy: Record<TimelineLanguage, LocalizedCopy>
}> = [
  {
    matches: (name) => name.endsWith('fundraising_context_get'),
    copy: {
      it: {
        title: 'Controllo il contesto dei dati',
        detail: 'Verifico periodo, copertura e limiti prima dell’analisi.',
      },
      en: {
        title: 'Checking the data context',
        detail: 'I am checking the period, coverage, and limits before the analysis.',
      },
    },
  },
  {
    matches: (name) => name.endsWith('fundraising_query'),
    copy: {
      it: {
        title: 'Consulto i dati di raccolta',
        detail: 'Calcolo i valori necessari per rispondere alla domanda.',
      },
      en: {
        title: 'Reviewing fundraising data',
        detail: 'I am calculating the values needed to answer the question.',
      },
    },
  },
  {
    matches: (name) => name.endsWith('data_lab_query'),
    copy: {
      it: {
        title: 'Preparo una vista dei dati',
        detail: 'Organizzo misure e gruppi in una lettura coerente.',
      },
      en: {
        title: 'Preparing a data view',
        detail: 'I am arranging measures and groups into a coherent view.',
      },
    },
  },
  {
    matches: (name) => name.endsWith('donor_get') || name.endsWith('donor_list'),
    copy: {
      it: {
        title: 'Consulto i dati dei donatori',
        detail: 'Recupero solo le informazioni necessarie per questa richiesta.',
      },
      en: {
        title: 'Reviewing donor data',
        detail: 'I am retrieving only the information needed for this request.',
      },
    },
  },
  {
    matches: (name) => name.includes('report_link_build'),
    copy: {
      it: {
        title: 'Preparo il report',
        detail: 'Creo un collegamento alla vista che sostiene la risposta.',
      },
      en: {
        title: 'Preparing the report',
        detail: 'I am creating a link to the view that supports the answer.',
      },
    },
  },
  {
    matches: (name) => name.includes('inbox_query'),
    copy: {
      it: {
        title: 'Controllo l’attività recente',
        detail: 'Leggo gli aggiornamenti utili dalla Inbox.',
      },
      en: {
        title: 'Checking recent activity',
        detail: 'I am reading the relevant updates from the Inbox.',
      },
    },
  },
  {
    matches: (name) => name.includes('inbox_reconcile') || name.includes('briefing_reconcile'),
    copy: {
      it: {
        title: 'Aggiorno la Inbox',
        detail: 'Allineo il risultato con gli elementi già presenti.',
      },
      en: {
        title: 'Updating the Inbox',
        detail: 'I am aligning the result with the existing items.',
      },
    },
  },
  {
    matches: (name) => name.includes('personal_job_schedule'),
    copy: {
      it: {
        title: 'Controllo la pianificazione',
        detail: 'Verifico le attività programmate e i prossimi avvii.',
      },
      en: {
        title: 'Checking the schedule',
        detail: 'I am checking scheduled work and upcoming runs.',
      },
    },
  },
  {
    matches: (name) => name.includes('personal_job_run'),
    copy: {
      it: {
        title: 'Controllo le esecuzioni',
        detail: 'Verifico lo stato delle attività avviate.',
      },
      en: {
        title: 'Checking current runs',
        detail: 'I am checking the status of started work.',
      },
    },
  },
  {
    matches: (name) => name.includes('feedback_send'),
    copy: {
      it: {
        title: 'Invio il feedback',
        detail: 'Registro il commento nel canale previsto.',
      },
      en: {
        title: 'Sending the feedback',
        detail: 'I am recording the comment in the selected channel.',
      },
    },
  },
  {
    matches: (name) => name.includes('feedback_list'),
    copy: {
      it: {
        title: 'Leggo i feedback',
        detail: 'Raccolgo le segnalazioni registrate dagli utenti.',
      },
      en: {
        title: 'Reading the feedback',
        detail: 'I am collecting the reports users have recorded.',
      },
    },
  },
  {
    matches: (name) => /search|browse|web/i.test(name),
    copy: {
      it: {
        title: 'Cerco le informazioni necessarie',
        detail: 'Consulto le fonti utili e verifico ciò che serve.',
      },
      en: {
        title: 'Finding the needed information',
        detail: 'I am reviewing useful sources and checking the relevant details.',
      },
    },
  },
]

const DRAWER_COPY = {
  label: 'Attività di Luma',
  headingRunning: 'Attività in corso',
  headingComplete: 'Attività svolte',
  close: 'Chiudi attività',
  understandRunning: 'Capisco la richiesta',
  understandComplete: 'Ho impostato il lavoro',
  understandDetail: 'Individuo i dati e i controlli necessari.',
  answerRunning: 'Preparo la risposta',
  answerComplete: 'Ho preparato la risposta',
  answerDetailRunning: 'Riunisco i risultati in una sintesi chiara.',
  answerDetailComplete: 'Ho riunito i risultati in una sintesi chiara.',
  statusRunning: 'In corso',
  statusError: 'Non completato',
  statusComplete: 'Completato',
  working: 'Sto lavorando…',
  thinking: 'Sto ragionando…',
  completedTrigger: 'Attività svolte',
  completedTriggerLabel: 'Mostra attività svolte',
  errorTrigger: 'Qualcosa non ha funzionato',
  timelineLabel: 'Aggiornamenti attività',
} as const

const ITALIAN_WORDS = new Set([
  'analizza', 'che', 'ciao', 'come', 'con', 'controlla', 'dati', 'dei', 'del', 'della',
  'dimmi', 'donatori', 'fammi', 'mostra', 'per', 'puoi', 'qual', 'questa', 'questo',
  'raccolta', 'sono', 'vorrei',
])
const ENGLISH_WORDS = new Set([
  'analyze', 'can', 'check', 'could', 'data', 'donors', 'from', 'fundraising', 'hello',
  'how', 'please', 'show', 'that', 'the', 'this', 'what', 'which', 'with', 'would',
])

function promptLanguage(prompt: string): TimelineLanguage {
  const words = prompt.toLocaleLowerCase().match(/\p{L}+/gu) ?? []
  const italian = words.filter((word) => ITALIAN_WORDS.has(word)).length
  const english = words.filter((word) => ENGLISH_WORDS.has(word)).length
  return english > italian ? 'en' : 'it'
}

export function activityTimelineCopy(_prompt: string) {
  return DRAWER_COPY
}

function truncate(text: string, limit: number): string {
  return text.length > limit ? `${text.slice(0, limit - 1).trimEnd()}…` : text
}

function cleanSummary(summary: string): LocalizedCopy | null {
  const lines = summary
    .split(/\n+/)
    .map((line) => line.replace(/[*_#>`]/g, '').replace(/\s+/g, ' ').trim())
    .filter(Boolean)
  const text = lines.join(' ')
  if (!text || /\b(?:mcp__|tool_use|command_execution)\b/i.test(text)) return null

  if (lines.length > 1) {
    return {
      title: truncate(lines[0], 120),
      detail: truncate(lines.slice(1).join(' '), 220),
    }
  }

  const sentence = text.match(/^(.{4,120}?[.!?])\s+(.+)$/u)
  if (sentence) {
    return {
      title: truncate(sentence[1], 120),
      detail: truncate(sentence[2], 220),
    }
  }

  return { title: truncate(text, 180), detail: '' }
}

function describeActivity(
  activity: ToolActivity,
  language: TimelineLanguage,
): TimelineStep | null {
  if (activity.text) {
    const copy = cleanSummary(activity.text)
    return copy ? {
      id: activity.id,
      title: copy.title,
      detail: copy.detail || undefined,
      status: activity.status,
    } : null
  }
  const copy = ACTIVITY_COPY.find((candidate) => candidate.matches(activity.name))
  if (!copy) return null
  return {
    id: activity.id,
    title: copy.copy[language].title,
    detail: copy.copy[language].detail,
    status: activity.status,
  }
}

function timelineSteps(
  activities: ToolActivity[],
  running: boolean,
  language: TimelineLanguage,
): TimelineStep[] {
  const copy = DRAWER_COPY
  const described = activities.flatMap((activity) => {
    const step = describeActivity(activity, language)
    return step ? [step] : []
  })
  const deduplicated = described.filter((step, index) => !described
    .slice(index + 1)
    .some((candidate) => candidate.title.toLocaleLowerCase(language)
      === step.title.toLocaleLowerCase(language)))
  const timeline = deduplicated
  const steps: TimelineStep[] = [
    {
      id: 'understand',
      title: timeline.length > 0 || !running
        ? copy.understandComplete
        : copy.understandRunning,
      detail: copy.understandDetail,
      status: timeline.length > 0 || !running ? 'completed' : 'running',
    },
    ...timeline,
  ]
  if (running && timeline.length > 0 && timeline.every(({ status }) => status !== 'running')) {
    steps.push({
      id: 'answer',
      title: copy.answerRunning,
      detail: copy.answerDetailRunning,
      status: 'running',
    })
  } else if (!running) {
    steps.push({
      id: 'answer',
      title: copy.answerComplete,
      detail: copy.answerDetailComplete,
      status: 'completed',
    })
  }
  return steps
}

function StepIcon({ status }: { status: TimelineStep['status'] }) {
  if (status === 'completed') return <Check size={13} aria-hidden />
  if (status === 'error') return <AlertCircle size={14} aria-hidden />
  return <Circle size={10} fill="currentColor" aria-hidden />
}

const FOLLOW_LATEST_THRESHOLD = 32

function useActivityTimelineScroll(contentVersion: string) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLOListElement>(null)
  const followsLatestRef = useRef(true)
  const didMountRef = useRef(false)
  const movementRef = useRef<Animation | null>(null)
  const [canScrollUp, setCanScrollUp] = useState(false)

  const handleScroll = useCallback(() => {
    const viewport = viewportRef.current
    if (!viewport) return
    const distanceFromBottom = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight
    if (distanceFromBottom <= FOLLOW_LATEST_THRESHOLD) followsLatestRef.current = true
    setCanScrollUp(viewport.scrollTop > 2)
  }, [])

  const pauseFollowing = useCallback(() => {
    const viewport = viewportRef.current
    if (viewport && viewport.scrollHeight > viewport.clientHeight) {
      followsLatestRef.current = false
      movementRef.current?.cancel()
    }
  }, [])

  const handleWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
    if (event.deltaY < 0) pauseFollowing()
  }, [pauseFollowing])

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (['ArrowUp', 'Home', 'PageUp'].includes(event.key)) pauseFollowing()
  }, [pauseFollowing])

  useLayoutEffect(() => {
    const viewport = viewportRef.current
    const content = contentRef.current
    if (!viewport || !content) return
    const currentHeight = viewport.scrollHeight
    if (!followsLatestRef.current) return

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
    const previousScrollTop = viewport.scrollTop
    viewport.scrollTop = currentHeight
    const movement = viewport.scrollTop - previousScrollTop
    movementRef.current?.cancel()
    if (
      didMountRef.current
      && !reduceMotion
      && movement > 0
      && typeof content.animate === 'function'
    ) {
      movementRef.current = content.animate(
        [
          { transform: `translateY(${movement}px)` },
          { transform: 'translateY(0)' },
        ],
        {
          duration: 260,
          easing: 'cubic-bezier(0.77, 0, 0.175, 1)',
          fill: 'both',
        },
      )
    }
    didMountRef.current = true
    setCanScrollUp(viewport.scrollTop > 2)
  }, [contentVersion])

  useEffect(() => () => movementRef.current?.cancel(), [])

  return {
    viewportRef,
    contentRef,
    canScrollUp,
    handleScroll,
    handleWheel,
    handleKeyDown,
    pauseFollowing,
  }
}

export function ActivityTimelineDrawer({
  activities,
  running,
  prompt,
  onClose,
}: {
  activities: ToolActivity[]
  running: boolean
  prompt: string
  onClose: () => void
}) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const language = promptLanguage(prompt)
  const copy = activityTimelineCopy(prompt)
  const steps = timelineSteps(activities, running, language)
  const contentVersion = steps
    .map((step) => `${step.id}:${step.title}:${step.detail ?? ''}:${step.status}`)
    .join('|')
  const timelineScroll = useActivityTimelineScroll(contentVersion)

  return (
    <aside className={styles.drawer} aria-label={copy.label}>
      <header className={styles.header}>
        <h2>{running ? copy.headingRunning : copy.headingComplete}</h2>
        <button type="button" className={styles.close} aria-label={copy.close} onClick={onClose}>
          <X size={17} aria-hidden />
        </button>
      </header>
      <div className={styles.timelineShell} data-can-scroll-up={timelineScroll.canScrollUp || undefined}>
        <div
          ref={timelineScroll.viewportRef}
          className={styles.timelineViewport}
          role="log"
          aria-label={copy.timelineLabel}
          aria-live="polite"
          aria-relevant="additions text"
          tabIndex={0}
          onScroll={timelineScroll.handleScroll}
          onWheel={timelineScroll.handleWheel}
          onPointerDown={timelineScroll.pauseFollowing}
          onTouchStart={timelineScroll.pauseFollowing}
          onKeyDown={timelineScroll.handleKeyDown}
        >
          <ol ref={timelineScroll.contentRef} className={styles.timeline}>
            {steps.map((step) => (
              <li
                key={step.id}
                className={styles.step}
                data-status={step.status}
                aria-label={`${step.title}. ${step.status === 'running' ? copy.statusRunning : step.status === 'error' ? copy.statusError : copy.statusComplete}`}
              >
                <span className={styles.marker}>
                  <StepIcon status={step.status} />
                </span>
                <div className={styles.stepCopy}>
                  <strong>{step.title}</strong>
                  {step.detail ? <span>{step.detail}</span> : null}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </aside>
  )
}
